import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Container, Stack, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { ClientUserGroupMember } from "../../../../api/types/userManagementTypes";
import AddUserGroupMembersDialog from "./AddUserGroupMembersDialog";
import UserGroupDetailsContext from "./UserGroupDetailsContext";
import { addMembersAction, filterAndSortMembers, updateNameAction } from "./userGroupDetailsState";
import UserGroupMembersGrid from "./UserGroupMembersGrid";

const UserGroupDetailsPageContainer = () => {
  const { state, setState, hasPermissionToManage, availableMembers } = useDefinedContext(UserGroupDetailsContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [isAddMembersDialogOpen, setIsAddMembersDialogOpen] = useState(false);

  const filteredMembers = useMemo(() => filterAndSortMembers(state.members, searchTerm), [state.members, searchTerm]);

  const memberOptions = useMemo(() => {
    const currentMemberIds = new Set(state.members.map((m) => m.id));
    return availableMembers.filter((m) => !currentMemberIds.has(m.id));
  }, [availableMembers, state.members]);

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim().toLocaleLowerCase());
  };

  const handleMembersAdded = (addedMembers: ClientUserGroupMember[]) => {
    setState(addMembersAction(addedMembers));
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ pt: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        <TextField
          label="Name"
          fullWidth
          value={state.name}
          onChange={(e) => setState(updateNameAction(e.target.value))}
          error={!!state.validationResult.errors.name}
          helperText={state.validationResult.errors.name}
        />

        <Typography variant="subtitle1" sx={{ mt: 2.5, py: 2 }}>
          Group Members
        </Typography>

        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", mb: 2 }}>
          <RecordCounter records={filteredMembers.length} totalRecords={state.members.length} />
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <SearchField debounceTimeMs={300} onSearch={handleSearch} />
            {hasPermissionToManage && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsAddMembersDialogOpen(true)}
                disabled={memberOptions.length === 0}
              >
                Add Members
              </Button>
            )}
          </Stack>
        </Box>

        <UserGroupMembersGrid rows={filteredMembers} />
      </Container>
      {isAddMembersDialogOpen && (
        <AddUserGroupMembersDialog
          memberOptions={memberOptions}
          onClose={() => setIsAddMembersDialogOpen(false)}
          onAdd={handleMembersAdded}
        />
      )}
    </>
  );
};

export default UserGroupDetailsPageContainer;
